import { useContext, useMemo } from "react";
import type { Context } from "react";
import * as PriceVisibility from "../contexts/PriceVisibilityContext";
import {
  SALES_MARKUP_OFF,
  SalesMarkup,
  salesLineTotal,
  salesMarkupFactor,
  salesPrice,
} from "../utils/functions/salesMarkup";

export interface SalesMarkupView {
  /** True only when the flag is on AND the dealer switched the markup on. */
  active: boolean;
  percent: number;
  factor: number;
  markup: SalesMarkup;
  /** Unit/line amount to show; customer commission wins (see salesPrice). */
  price: {
    (amount: number, customerAmount?: number | null): number;
    (
      amount: number | undefined,
      customerAmount?: number | null,
    ): number | undefined;
  };
  /** Line total to show, scaled like its unit price. */
  lineTotal: (
    lineTotal: number,
    unitPrice?: number | null,
    customerUnitPrice?: number | null,
  ) => number;
}

type ContextShape = { salesMarkup?: SalesMarkup } | undefined;

/**
 * "Sales price markup" (flag priceMarkupEnabled): the one place every price
 * surface asks "what do I show for this purchase price?". Reads the
 * PriceVisibilityContext's salesMarkup (present only with the flag on) and
 * is inert otherwise: active=false, factor=1, price(x)=x - so a component
 * that adopts it renders byte-identically while the flag is off.
 *
 * Deliberately tolerant: no provider above (a public page, a test) and test
 * doubles of the context module that export only usePriceVisibility both
 * read as "off" instead of throwing.
 */
export function useSalesMarkup(): SalesMarkupView {
  const Ctx = (
    PriceVisibility as unknown as {
      PriceVisibilityContext?: Context<ContextShape>;
    }
  ).PriceVisibilityContext;
  // The export either exists for the whole session or never does, so the
  // hook order is stable.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const ctx = Ctx ? useContext(Ctx) : undefined;
  const markup = ctx?.salesMarkup?.enabled ? ctx.salesMarkup : SALES_MARKUP_OFF;

  return useMemo<SalesMarkupView>(() => {
    const price = ((amount: number | undefined, customerAmount?: number | null) =>
      salesPrice(amount, markup, customerAmount)) as SalesMarkupView["price"];
    return {
      active: markup.enabled,
      percent: markup.percent,
      factor: salesMarkupFactor(markup),
      markup,
      price,
      lineTotal: (lineTotal, unitPrice, customerUnitPrice) =>
        salesLineTotal(lineTotal, markup, unitPrice, customerUnitPrice),
    };
  }, [markup]);
}
